import React, { useEffect, memo } from 'react';
import { Text } from '@cuemath/web-comps';
import { Separator } from '@cuemath/comps';

import { useFunnelSummary, useFunnelTimeseries } from '../api/get-funnel';
import KpiCards from '../components/kpi-cards';
import FunnelTable, { buildSourceColumns } from '../components/funnel-table';
import FunnelBarChart from '../components/charts/funnel-bar-chart';
import ConversionLineChart from '../components/charts/conversion-line-chart';
import {
  Card,
  SectionLabel,
  ChartBox,
  Legend,
  LegendItem,
  Dot,
  StateBlock,
  UI,
} from '../styled-comps';
import { METRICS, CONVERSIONS, EMPTY_MESSAGE, COMPARE_ERROR_MESSAGE } from '../constants';
import {
  asRows,
  metricFields,
  sumRows,
  pivotTimeseries,
  totalPerMonth,
  conversionPct,
  formatMonthLabel,
  channelLabel,
  channelColor,
} from '../helpers';
import { useSettledKey } from '../hooks/use-settled-key';

// Legend built from the channels the DB actually returned.
const SourceLegend = ({ channels }) => (
  <Legend>
    {channels.map(channel => (
      <LegendItem key={channel}>
        <Dot $color={channelColor(channel)} />
        <Text variant="caption" color={UI.body}>{channelLabel(channel)}</Text>
      </LegendItem>
    ))}
  </Legend>
);

const qlDatasetsFromPivot = pivot =>
  pivot.channels.map(channel => ({
    label: channelLabel(channel),
    data: pivot.series[channel][METRICS.QL],
    color: channelColor(channel),
  }));

// Summary tab — the ONLY tab with charts (timeseries queries run just here).
// With a comparison active, every table cell shows the exact comparison value
// and a second chart renders the comparison period.
const Summary = memo(({ primaryQuery, compareQuery, primaryKey, compareKey }) => {
  const { data: sumData, get: getSummary, isProcessing: sumLoading, error: sumError } = useFunnelSummary();
  const { data: cmpData, get: getCompare, error: cmpError } = useFunnelSummary();
  const { data: tsData, get: getTimeseries, isProcessing: tsLoading, error: tsError } = useFunnelTimeseries();
  const { data: tsCmpData, get: getTimeseriesCmp, error: tsCmpError } = useFunnelTimeseries();

  const hasCompare = !!compareQuery;

  useEffect(() => {
    getSummary('wm-summary', primaryQuery);
    getTimeseries('wm-summary-ts', primaryQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryKey]);

  useEffect(() => {
    if (compareQuery) {
      getCompare('wm-summary-cmp', compareQuery);
      getTimeseriesCmp('wm-summary-ts-cmp', compareQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareKey]);

  // True only once each response reflects the applied primary query — so the
  // source table, KPI cards and charts never render the previous range's numbers
  // for the render between Apply and the store swapping in the new resource.
  const freshSum = useSettledKey(primaryKey, sumLoading, sumError);
  const freshTs = useSettledKey(primaryKey, tsLoading, tsError);

  const rows = freshSum ? asRows(sumData) : [];
  const cmpRows = hasCompare ? asRows(cmpData) : [];
  const totals = sumRows(rows);
  const compareTotals = hasCompare ? sumRows(cmpRows) : null;

  // Attach comparison metrics per channel for the source table cells.
  const cmpByChannel = {};
  cmpRows.forEach(row => {
    cmpByChannel[row.channel] = metricFields(row);
  });
  const sourceRows = rows.map(row =>
    hasCompare ? { ...row, _cmp: cmpByChannel[row.channel] || metricFields({}) } : row,
  );

  const pivot = pivotTimeseries(asRows(freshTs ? tsData : []));
  const labels = pivot.months.map(formatMonthLabel);
  const cmpPivot = hasCompare ? pivotTimeseries(asRows(tsCmpData)) : null;

  const metricMonthly = {
    [METRICS.QL]: totalPerMonth(pivot, METRICS.QL),
    [METRICS.TRIAL_DONE]: totalPerMonth(pivot, METRICS.TRIAL_DONE),
    [METRICS.ENROLLED]: totalPerMonth(pivot, METRICS.ENROLLED),
  };
  const convDatasets = CONVERSIONS.map(conv => ({
    label: conv.label,
    color: conv.color,
    data: pivot.months.map((_, i) =>
      conversionPct(metricMonthly[conv.numerator][i], metricMonthly[conv.denominator][i]),
    ),
  }));

  const compareRangeText = hasCompare
    ? `${compareQuery.start_date} → ${compareQuery.end_date}`
    : '';
  const compareError = hasCompare ? cmpError || tsCmpError : null;

  if (sumError) {
    return (
      <StateBlock>
        <Text variant="body1" color={UI.down}>{sumError}</Text>
      </StateBlock>
    );
  }

  if ((sumLoading || !freshSum) && !rows.length) {
    return (
      <StateBlock>
        <Text variant="body1" color={UI.muted}>Loading website metrics…</Text>
      </StateBlock>
    );
  }

  if (!sumLoading && freshSum && !rows.length) {
    return (
      <StateBlock>
        <Text variant="body1" color={UI.muted}>{EMPTY_MESSAGE}</Text>
      </StateBlock>
    );
  }

  return (
    <>
      <SectionLabel>
        Funnel overview{hasCompare ? ` — vs ${compareRangeText}` : ''}
      </SectionLabel>
      <KpiCards totals={totals} compareTotals={compareTotals} />
      {compareError && (
        <Text variant="caption" color={UI.down}>{COMPARE_ERROR_MESSAGE}</Text>
      )}

      <SectionLabel>QL by source — month on month (selected date range)</SectionLabel>
      <Card>
        <Text variant="body2" color={UI.ink}>Qualified Leads by organic source</Text>
        <Separator height={10} transparent />
        <SourceLegend channels={pivot.channels} />
        <ChartBox $height={280}>
          {tsError ? (
            <Text variant="body3" color={UI.down}>{tsError}</Text>
          ) : labels.length ? (
            <FunnelBarChart labels={labels} datasets={qlDatasetsFromPivot(pivot)} stacked />
          ) : (
            <Text variant="body3" color={UI.muted}>{tsLoading || !freshTs ? 'Loading…' : EMPTY_MESSAGE}</Text>
          )}
        </ChartBox>
      </Card>

      {hasCompare && cmpPivot && (
        <>
          <SectionLabel>{`QL by source — comparison period (${compareRangeText})`}</SectionLabel>
          <Card>
            <Text variant="body2" color={UI.ink}>Qualified Leads by organic source — comparison</Text>
            <Separator height={10} transparent />
            <SourceLegend channels={cmpPivot.channels} />
            <ChartBox $height={280}>
              {cmpPivot.months.length ? (
                <FunnelBarChart
                  labels={cmpPivot.months.map(formatMonthLabel)}
                  datasets={qlDatasetsFromPivot(cmpPivot)}
                  stacked
                />
              ) : (
                <Text variant="body3" color={UI.muted}>{EMPTY_MESSAGE}</Text>
              )}
            </ChartBox>
          </Card>
        </>
      )}

      <SectionLabel>Conversion rates (selected date range)</SectionLabel>
      <Card>
        <Text variant="body2" color={UI.ink}>Funnel efficiency across all sources</Text>
        <Separator height={10} transparent />
        <ChartBox $height={220}>
          {labels.length ? (
            <ConversionLineChart labels={labels} datasets={convDatasets} />
          ) : (
            <Text variant="body3" color={UI.muted}>{tsLoading || !freshTs ? 'Loading…' : EMPTY_MESSAGE}</Text>
          )}
        </ChartBox>
      </Card>

      <SectionLabel>
        Source-level breakdown{hasCompare ? ` — each cell shows "vs comparison" (${compareRangeText})` : ''}
      </SectionLabel>
      <FunnelTable
        data={sourceRows}
        columns={buildSourceColumns(totals[METRICS.QL])}
        loading={sumLoading}
        defaultSorted={[{ id: METRICS.QL, desc: true }]}
      />
    </>
  );
});

export default Summary;
